import React, { useState, useEffect } from 'react';
import { Container, Row, Col, ListGroup, Card, Modal, Button, Badge } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, useMap, GeoJSON } from 'react-leaflet';
import Sidebar from '../../../../Components/Sidebar';
import { useNavigate } from 'react-router-dom';
import getEnvConfig from '../../../../config';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FindChargers = () => {
  const { backendUrl } = getEnvConfig();
  const [showModal, setShowModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [portsAtLocation, setPortsAtLocation] = useState([]);
  const navigate = useNavigate();
  
  // Custom markers
  const greenMarker = new L.Icon({
    iconUrl: "/images/Gps-green.png",        
    iconColor: 'green',
    iconSize: [20, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40]
  });

  const redMarker = new L.Icon({
    iconUrl: "/images/Gps-red.png",        
    iconColor: 'red',
    iconSize: [20, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40]
  });
  
  const yellowMarker = new L.Icon({
    iconUrl: "/images/Gps-yellow.png",        
    iconColor: 'yellow',
    iconSize: [20, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40]
  });

  const blueMarker = new L.Icon({
    iconUrl: "/images/Gps-blue.png",     
    iconColor: 'blue',
    iconSize: [20, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40]
  });

  // Helper to get marker based on port status
  const getMarkerForStatus = (status) => {
    if (!status) return greenMarker;
    
    status = status.toLowerCase();
    
    if (status === 'available') return greenMarker;
    if (status === 'charging' || status === 'occupied' || status === 'busy') return yellowMarker;
    if (status === 'offline' || status === 'faulted') return redMarker;
    return greenMarker;
  };

  // Handle port selection to navigate to charger details with port_id
  const handlePortSelect = (portId) => {
    navigate(`/charger/port/${portId}`);
    setShowModal(false);
  };

  // Group ports by location to identify locations with multiple ports
  const groupPortsByLocation = (features) => {
    if (!features) return {};
    
    const locationGroups = {};
    
    features.forEach(feature => {
      const { loc_id } = feature.properties;
      if (!locationGroups[loc_id]) {
        locationGroups[loc_id] = [];
      }
      locationGroups[loc_id].push(feature);
    });
    
    return locationGroups;
  };

  function LocationMarker() {
    const [position, setPosition] = useState(null);
    const map = useMap();

    useEffect(() => {
      map.locate().on("locationfound", function (e) {
        setPosition(e.latlng);
        map.flyTo(e.latlng, map.getZoom());
      });
    }, [map]);

    return position === null ? null : (
      <Marker position={position} icon={blueMarker}>
        <Popup>You are here</Popup>
      </Marker>
    );
  }

  function ChargerLocationMarker() {
    const [chargerData, setChargerData] = useState(null);
    const map = useMap();
    
    useEffect(() => {
      map.locate().on("locationfound", function (e) {
        const { lat, lng } = e.latlng;
        fetchChargers(lng, lat);
      });
    }, [map]);

    const fetchChargers = async (lng, lat) => {
      try {
        const response = await fetch(`${backendUrl}/chargers/listings/${lng},${lat}`, {
          method: "GET", 
        });
        const reply = await response.json();
        
        if (response.ok) {     
          setChargerData(reply['chargers']);
          console.log('Charger data:', reply);
        }
      } catch (error) {
        console.error('Error fetching charger data:', error);
      }
    };

    if (!chargerData) return null;
    
    // Group ports by location to identify which locations have multiple ports
    const locationGroups = groupPortsByLocation(chargerData.features);

    return (
      <GeoJSON 
        data={chargerData} 
        pointToLayer={(feature, latlng) => {
          const locId = feature.properties.loc_id;
          const isMultiPort = locationGroups[locId] && locationGroups[locId].length > 1;
          const status = feature.properties.port_status;
          
          // Choose marker based on status
          const marker = L.marker(latlng, { 
            icon: getMarkerForStatus(status),
            // Store metadata about the location
            locationData: {
              locId,
              isMultiPort,
              ports: locationGroups[locId]
            }
          });
          
          // For single port locations, direct link
          if (!isMultiPort) {
            const popupContent = `
              <div>
                <h6>Port #${feature.properties.port_number}</h6>
                <p>Connector: ${feature.properties.connector_type_name || 'Unknown'}</p>
                <p>Power: ${feature.properties.max_power_kw || 'N/A'} kW</p>
                <p>Status: ${feature.properties.port_status || 'Unknown'}</p>
                <a href="/charger/port/${feature.properties.port_id}" class="btn btn-primary btn-sm"><p>View Charger </p></a>
              </div>
            `;
            marker.bindPopup(popupContent);
          } else {
            // For multi-port locations, show modal when clicked
            marker.on('click', function() {
              setSelectedLocation({
                id: locId,
                latitude: feature.properties.latitude,
                longitude: feature.properties.longitude
              });
              setPortsAtLocation(locationGroups[locId]);
              setShowModal(true);
            });
            
            // Still show a basic popup
            const popupContent = `
              <div>
                <strong>Multiple Ports Available</strong>
                <p>Click to view options</p>
              </div>
            `;
            marker.bindPopup(popupContent);
          }
          
          return marker;
        }}
      />
    );
  }

  const getConnectorTypeBadgeColor = (connectorTypeId) => {
    // Define badge colors for different connector types
    switch(connectorTypeId) {
      case 1: return 'primary'; // J1772
      case 2: return 'success'; // CCS
      case 3: return 'danger';  // CHAdeMO
      case 4: return 'warning'; // NACS
      default: return 'secondary';
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <Container fluid style={{ marginLeft: '5px' }}>
        <Row>
          <Col className="container col-xxl-8 px-3 py-3 d-flex align-items-center justify-content-center">
            <span className="text-center">
              <h4>Search for Charging Stations</h4>
              <p>Find and book the nearest EV charging stations available in your area.</p>
            </span>
          </Col>
        </Row>
        <Row>
          <div className="container col-xxl-8 px-3 py-3 d-flex align-items-center justify-content-center" style={{ height: '600px', width: '100%' }}>
            <MapContainer
              center={[37, -95.7]}
              zoom={9}
              maxZoom={19}
              scrollWheelZoom
              style={{ height: "600px", width: "100%" }}
              className="custom-map-container"
            >
              <TileLayer
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationMarker />
              <ChargerLocationMarker />
            </MapContainer>
          </div>
        </Row>

        {/* Modal for multi-port selection */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Select a Charging Port</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ListGroup>
              {portsAtLocation.map((feature, index) => {
                const port = feature.properties;
                return (
                  <ListGroup.Item 
                    key={index}
                    action
                    onClick={() => handlePortSelect(port.port_id)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <h6>Port #{port.port_number}</h6>
                      <div>
                        <Badge 
                          bg={getConnectorTypeBadgeColor(port.connector_type_id)}
                          className="me-2"
                        >
                          {port.connector_type_name || `Type ${port.connector_type_id}`}
                        </Badge>
                        <Badge 
                          bg={port.port_status === 'available' ? 'success' : 
                              port.port_status === 'charging' ? 'danger' : 'warning'}
                        >
                          {port.port_status || 'Unknown'}
                        </Badge>
                      </div>
                      <small className="text-muted">{port.max_power_kw || 'N/A'} kW</small>
                    </div>
                    <Button variant="outline-primary" size="sm">Select</Button>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default FindChargers;
